import { type JSX, type ReactNode, useSyncExternalStore } from 'react';
import { useAuth } from '@frontegg/nextjs';
import {
  getFrontendConfigLoader,
  getGrowthBookConfigLoader,
} from '@doxyme/config';
import {
  HotpotProvider,
  PatientHotpotProvider,
  ClinicHotpotProvider,
  version,
} from '@doxyme/hotpot/react';
import { getLogger } from '../../libs/logger';
import { useAppSelector } from '../../hooks/useReduxToolkit';
import { getWaitingRoom } from '../../features/waitingRoom/waitingRoom.selectors';

const hotpotLogger = getLogger('hotpot', {
  package: 'hotpot',
  packageVersion: version,
});
hotpotLogger.setLevel('info');

const useIsHotpotEnabled = () =>
  useSyncExternalStore(
    getGrowthBookConfigLoader().subscribe.bind(getGrowthBookConfigLoader()),
    () => getGrowthBookConfigLoader().getOptional('is_hotpot_enabled') === true,
  );

type HotpotProviderWrapperProps = {
  children: ReactNode;
};

/**
 * A wrapper for the Hotpot provider that enables/disables it based on the growth book config.
 * Also checks if the hotpot url is set.
 */
export function HotpotProviderWrapper({
  children,
}: HotpotProviderWrapperProps): JSX.Element {
  const hotpotUrl = getFrontendConfigLoader().get('HOTPOT_URL') as
    | string
    | undefined;

  return useIsHotpotEnabled() && !!hotpotUrl ? (
    <HotpotProvider hotpotUrl={hotpotUrl} logger={hotpotLogger}>
      {children}
    </HotpotProvider>
  ) : (
    <>{children}</>
  );
}

/**
 * A wrapper for the PatientHotpot provider that enables/disables it based on the growth book config.
 * Also checks if the clinic id is set.
 */
export function PatientHotpotProviderWrapper({
  children,
}: HotpotProviderWrapperProps): JSX.Element {
  const room = useAppSelector(getWaitingRoom);
  const clinicId = room?.roomAdmin?.authId ?? undefined;

  return useIsHotpotEnabled() && !!clinicId ? (
    <PatientHotpotProvider clinicId={clinicId}>
      {children}
    </PatientHotpotProvider>
  ) : (
    <>{children}</>
  );
}

/**
 * A wrapper for the ClinicHotpot provider that enables/disables it based on the growth book config.
 */
export function ClinicHotpotProviderWrapper({
  children,
}: HotpotProviderWrapperProps): JSX.Element {
  return useIsHotpotEnabled() ? (
    <ClinicHotpotProvider useAuth={useAuth}>{children}</ClinicHotpotProvider>
  ) : (
    <>{children}</>
  );
}
